import { useMemo, useState } from "react";

import { AccepterModal } from "src/core/components/folder/footer/AccepterModal";
import { FolderFooterUI } from "src/core/components/folder/footer/FolderFooterUI";
import { useFolderFooter } from "src/core/hooks/folder/footer/useFolderFooter";
import { useFolderRisk } from "src/core/hooks/folder/useFolderRisk";
import { cn } from "src/core/lib/utils";

export type AccepterModalActionType = "accept" | "refuse";

type FolderFooterECProps = {
  onAfterValidationRefetch?: () => void;
};

export const FolderFooterEC = ({ onAfterValidationRefetch }: FolderFooterECProps = {}) => {
  const {
    isDraft,
    isValidation,
    isValidated,
    isRejected,
    isReexaminable,
    displayFooter,
    isModulationInProgress,
  } = useFolderFooter();

  const globalRisk = useFolderRisk("global");
  const [openAction, setOpenAction] = useState<boolean>(isReexaminable && displayFooter);

  const [showAccepterModal, setShowAccepterModal] = useState<boolean>(false);
  const [modalActionType, setModalActionType] = useState<AccepterModalActionType>("accept");

  const openAccepterModal = (type: AccepterModalActionType) => {
    setModalActionType(type);
    setShowAccepterModal(true);
  };

  const content = useMemo(() => {
    if (isDraft && displayFooter) {
      return (
        <FolderFooterUI.Root className="bg-foreground">
          <div className="grid w-full grid-cols-12 items-center">
            <div className="col-span-3">
              <FolderFooterUI.DeleteFolderAction variant="linkWhite" />
            </div>
            <div className="col-span-7 flex items-center justify-center gap-x-20">
              <FolderFooterUI.MissingFields />
              <FolderFooterUI.ReturnReasonAction />
              <FolderFooterUI.SubmitForValidationAction variant="secondary" />
              <FolderFooterUI.LastSaveInfo />
            </div>
            <div className="col-span-2 justify-self-end">
              <FolderFooterUI.Action
                variantVersion="linkWhite"
                open={openAction}
                setOpen={setOpenAction}
              />
            </div>
          </div>
        </FolderFooterUI.Root>
      );
    }

    if (isValidation && displayFooter) {
      return (
        <FolderFooterUI.Root className="bg-foreground">
          <div className="grid w-full grid-cols-12 items-center">
            <div className="col-span-3">
              <FolderFooterUI.SendBackToDraftAction />
            </div>
            <div className="col-span-6 flex items-center justify-center gap-x-20">
              <FolderFooterUI.ValidationActions
                variant="secondary"
                onOpenAccepterModal={openAccepterModal}
              />
              <FolderFooterUI.LastSaveInfo />
            </div>
            <div className="col-span-3 justify-self-end">
              <FolderFooterUI.Action
                variantVersion="linkWhite"
                open={openAction}
                setOpen={setOpenAction}
              />
            </div>
          </div>
        </FolderFooterUI.Root>
      );
    }

    if (isReexaminable && displayFooter) {
      return (
        <FolderFooterUI.Root
          data-risk={globalRisk}
          className={cn(
            "data-[risk=LOW]:bg-green-excel",
            "data-[risk=MEDIUM]:bg-orange-medium",
            "data-[risk=NORMAL]:bg-orange-medium",
            "data-[risk=HIGH]:bg-red-badge"
          )}
        >
          <div className="grid w-full grid-cols-12 items-center">
            <div className="col-span-3"></div>
            <div className="col-span-6 flex items-center justify-center gap-x-20">
              <FolderFooterUI.CloseMissionAction />
              <FolderFooterUI.ReexaminationAction />
              <FolderFooterUI.LastSaveInfo />
            </div>
            <div className="col-span-3 justify-self-end">
              <FolderFooterUI.Action
                variantVersion="linkWhite"
                withExportPdfFolder={true}
                open={openAction}
                setOpen={setOpenAction}
              />
            </div>
          </div>
        </FolderFooterUI.Root>
      );
    }

    if (isValidated || isRejected) {
      return (
        <FolderFooterUI.Root
          data-risk={globalRisk}
          className={cn(
            "data-[risk=LOW]:bg-green-excel",
            "data-[risk=MEDIUM]:bg-orange-medium",
            "data-[risk=NORMAL]:bg-orange-medium",
            isValidated && "data-[risk=HIGH]:bg-red-badge",
            isRejected && "data-[risk=HIGH]:bg-red-medium"
          )}
        >
          <div className="grid w-full grid-cols-12 items-center">
            <div className="col-span-3"></div>
            <div className="col-span-6 flex items-center justify-center gap-x-20">
              <FolderFooterUI.CurrentVersionLink variant="tertiaryWhite" />
              <FolderFooterUI.LastSaveInfo />
            </div>
            <div className="col-span-3 justify-self-end">
              <FolderFooterUI.Action
                variantVersion="linkWhite"
                open={openAction}
                setOpen={setOpenAction}
                withExportPdfFolder={true}
              />
            </div>
          </div>
        </FolderFooterUI.Root>
      );
    }

    return null;
  }, [
    isDraft,
    isValidation,
    isReexaminable,
    isValidated,
    isRejected,
    globalRisk,
    openAction,
    displayFooter,
  ]);

  return (
    <>
      {content}

      <AccepterModal
        open={showAccepterModal}
        setOpen={setShowAccepterModal}
        actionType={modalActionType}
        onAfterRefetch={onAfterValidationRefetch}
        isModulationInProgress={isModulationInProgress}
      />
    </>
  );
};
